import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import GroupCreationModal from './GroupCreationModal';
import '../styles/Groups.css';
import { loadHiddenGroups, loadReadOnlyGroups, saveReadOnlyGroups, unhideGroup } from '../utils/groupListStorage';

function Groups({ user, socket, onGroupSelect, groupNotificationCounts = {}, refreshGroupNotificationCount }) {
  const [groups, setGroups] = useState([]);
  const [readOnlyGroups, setReadOnlyGroups] = useState(() => loadReadOnlyGroups());
  const [hiddenGroups, setHiddenGroups] = useState(() => loadHiddenGroups());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchGroups();
    }
  }, [user]);

  useEffect(() => {
    const onListChange = () => {
      setHiddenGroups(loadHiddenGroups());
      setReadOnlyGroups(loadReadOnlyGroups());
      fetchGroups();
    };
    window.addEventListener('groupListStorageUpdated', onListChange);
    return () => window.removeEventListener('groupListStorageUpdated', onListChange);
  }, [user]);

  // Move a group to the top when it gets a new message
  const moveGroupToTop = (groupId) => {
    if (!groupId) return;
    const id = groupId.toString();
    setGroups((prev) => {
      const idx = prev.findIndex((g) => g._id?.toString() === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      const [group] = next.splice(idx, 1);
      return [{ ...group, updatedAt: new Date().toISOString() }, ...next];
    });
  };

  useEffect(() => {
    if (!socket) return;

    const handleGroupMessageNotification = (data) => {
      const message = data?.message;
      if (message?.messageType === 'system' || message?.messageType === 'call') return;
      moveGroupToTop(data?.groupId || message?.groupId);
    };

    const handleReceiveGroupMessage = (message) => {
      if (!message?.groupId) return;
      if (message.messageType === 'system' || message.messageType === 'call') return;
      moveGroupToTop(message.groupId);
    };

    socket.on('groupMessageNotification', handleGroupMessageNotification);
    socket.on('receiveGroupMessage', handleReceiveGroupMessage);
    return () => {
      socket.off('groupMessageNotification', handleGroupMessageNotification);
      socket.off('receiveGroupMessage', handleReceiveGroupMessage);
    };
  }, [socket]);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await axios.get(`${config.apiUrl}/api/groups/user/${user.id}`);
      const liveGroups = response.data || [];
      // Merge read-only groups that user was removed from (keep for viewing)
      const readOnlyList = Object.values(readOnlyGroups);
      // Avoid duplicates if user re-joined later
      const liveIds = new Set(liveGroups.map(g => g._id));

      // If a group is now live again (user re-joined), remove its read-only flag
      if (readOnlyList.length > 0) {
        setReadOnlyGroups(prev => {
          const next = { ...prev };
          let changed = false;
          readOnlyList.forEach(g => {
            if (liveIds.has(g._id) && next[g._id]) {
              delete next[g._id];
              changed = true;
            }
          });
          if (changed) {
            saveReadOnlyGroups(next);
          }
          return next;
        });
      }

      const hiddenIds = new Set(Object.keys(loadHiddenGroups()));
      const merged = [...liveGroups, ...readOnlyList.filter(g => !liveIds.has(g._id))]
        .filter((g) => !hiddenIds.has(g._id));
      setGroups(merged);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to fetch groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroupCreated = (newGroup) => {
    setGroups(prev => [newGroup, ...prev]);
  };

  const handleGroupUpdated = (updatedGroup) => {
    setGroups(prev => prev.map(group => 
      group._id === updatedGroup._id ? updatedGroup : group
    ));
    // If user re-joined, remove read-only flag for that group
    setReadOnlyGroups(prev => {
      const next = { ...prev };
      if (next[updatedGroup._id]) {
        // If the current user is present in updatedGroup.members, clear read-only
        const isMember = Array.isArray(updatedGroup.members) && updatedGroup.members.some(m => (m.user?._id || m.user || m)._id ? (m.user?._id || m.user || m)._id === user.id : (m.user?.toString?.() || m.toString?.()) === user.id);
        if (isMember) {
          delete next[updatedGroup._id];
          saveReadOnlyGroups(next);
          unhideGroup(updatedGroup._id);
          setHiddenGroups(loadHiddenGroups());
        }
      }
      return next;
    });
  };

  const removeGroupFromList = (groupId) => {
    setGroups((prev) => prev.filter((g) => g._id !== groupId));
    setReadOnlyGroups((prev) => {
      if (!prev[groupId]) return prev;
      const next = { ...prev };
      delete next[groupId];
      saveReadOnlyGroups(next);
      return next;
    });
  };

  const handleGroupClick = (group) => {
    if (onGroupSelect) {
      onGroupSelect(group, handleGroupUpdated, removeGroupFromList);
    }
  };

  if (isLoading) {
    return (
      <div className="groups-loading">
        <div className="loading-spinner"></div>
        <p>Loading groups...</p>
      </div>
    );
  }

  return (
    <div className="groups-container">
      <div className="groups-header">
        <div className="groups-title">
          <h2>Group</h2>
          {/* <p>Manage your group chats</p> */}
        </div>
        <button 
          className="create-group-button"
          aria-label="Create new group"
          onClick={() => setShowCreateModal(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="8" r="3"/>
            <path d="M4 19a5 5 0 0 1 10 0"/>
            <path d="M19 8v6M16 11h6"/>
          </svg>
          <span className="create-group-label">New Group</span>
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchGroups} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="groups-content">
        {!Array.isArray(groups) || groups.length === 0 ? (
          <div className="no-groups">
            <div className="no-groups-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                <path d="M16 3.13a4 4 0 010 7.75"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </div>
            <h3>No groups yet</h3>
            <p>Create your first group to start chatting with multiple contacts</p>
            <button 
              className="create-first-group-button"
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First Group
            </button>
          </div>
        ) : (
          <div className="groups-list">
            {Array.isArray(groups) && groups.map(group => (
              <div
                key={group._id}
                className="group-item"
                onClick={() => handleGroupClick(group)}
              >
                <div className="group-avatar notranslate">
                  {group.avatar ? (
                    <img 
                      src={`${config.apiUrl}${group.avatar}`} 
                      alt={group.name}
                      className="group-profile-image"
                    />
                  ) : (
                    <div className="group-avatar-placeholder">
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="group-info">
                  <div className="group-name notranslate">{group.name}</div>
                  {readOnlyGroups[group._id] && (
                    <span className="group-badge readonly">Read-only</span>
                  )}
                  {groupNotificationCounts[group._id] && (
                    <span className="group-notification-count">
                      {groupNotificationCounts[group._id]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <GroupCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGroupCreated={handleGroupCreated}
        user={user}
        socket={socket}
      />
    </div>
  );
}

export default Groups;

